import { authenticatedFetch } from '../config';

declare const __APP_CONFIG__: any;

export type QuadCoordinates = [
  [number, number],
  [number, number],
  [number, number],
  [number, number]
];

/**
 * Detects whether a decoded single-image WebP overlay is already warped to Web Mercator (EPSG:3857)
 * (e.g. 2048x1699 for regional [-170, 0, -30, 75] or 2048x2043 for global [-180, -85, 180, 85])
 * versus linear Plate Carrée (EPSG:4326) (e.g. 1401x751 regional or 1440x681 global).
 */
export function isAlreadyMercatorProjected(
  width: number,
  height: number,
  coordinates: QuadCoordinates
): boolean {
  const westLon = coordinates[0][0];
  const northLat = coordinates[0][1];
  const eastLon = coordinates[2][0];
  const southLat = coordinates[2][1];

  if (width <= 0 || height <= 0 || eastLon <= westLon || northLat <= southLat) {
    return true;
  }
  if (northLat - southLat <= 2.0) {
    return true;
  }

  const clampedNorth = Math.max(-85.051129, Math.min(85.051129, northLat));
  const clampedSouth = Math.max(-85.051129, Math.min(85.051129, southLat));
  const radNorth = (clampedNorth * Math.PI) / 180.0;
  const radSouth = (clampedSouth * Math.PI) / 180.0;
  const yMax = Math.log(Math.tan(Math.PI / 4.0 + radNorth / 2.0));
  const yMin = Math.log(Math.tan(Math.PI / 4.0 + radSouth / 2.0));
  const lonSpanRad = ((eastLon - westLon) * Math.PI) / 180.0;
  if (lonSpanRad <= 1e-6) {
    return true;
  }

  const mercatorAspect = (yMax - yMin) / lonSpanRad;
  const equirectAspect = (clampedNorth - clampedSouth) / (eastLon - westLon);
  if (Math.abs(mercatorAspect - equirectAspect) < 0.02) {
    return true;
  }

  const actualAspect = height / width;
  const distToMercator = Math.abs(actualAspect - mercatorAspect);
  const distToEquirect = Math.abs(actualAspect - equirectAspect);
  return distToMercator <= distToEquirect && distToMercator < 0.12;
}

class ImageBitmapCacheManager {
  private cache = new Map<string, ImageBitmap>();
  private inFlight = new Map<string, Promise<ImageBitmap | null>>();
  private failedUrls = new Set<string>();
  private boundsCache = new Map<string, QuadCoordinates>();
  private boundsInFlight = new Map<string, Promise<QuadCoordinates | null>>();
  private maxEntries: number;

  constructor() {
    const cfgMax =
      typeof __APP_CONFIG__ !== 'undefined'
        ? __APP_CONFIG__?.ui?.forecast_animation?.bitmap_cache_max_entries
        : undefined;
    this.maxEntries = typeof cfgMax === 'number' && cfgMax > 16 ? cfgMax : 96;
  }

  /**
   * Synchronously returns a decoded ImageBitmap if already cached in memory.
   */
  public get(url: string): ImageBitmap | null {
    if (!url) return null;
    const existing = this.cache.get(url);
    if (existing) {
      // Refresh LRU position
      this.cache.delete(url);
      this.cache.set(url, existing);
      return existing;
    }
    return null;
  }

  public hasFailed(url: string): boolean {
    return this.failedUrls.has(url);
  }

  /**
   * Asynchronously fetches and decodes a WebP image off the main UI thread via createImageBitmap().
   */
  public async load(url: string, signal?: AbortSignal): Promise<ImageBitmap | null> {
    if (!url || this.failedUrls.has(url)) return null;

    const cached = this.get(url);
    if (cached) return cached;

    const existingPromise = this.inFlight.get(url);
    if (existingPromise) return existingPromise;

    const promise = (async (): Promise<ImageBitmap | null> => {
      try {
        const res = await authenticatedFetch(url, signal ? { signal } : undefined);
        if (!res.ok) {
          if (res.status === 404 || res.status === 403) {
            this.failedUrls.add(url);
          }
          return null;
        }
        const blob = await res.blob();
        if (!blob || blob.size === 0) {
          return null;
        }

        let bitmap: ImageBitmap;
        try {
          bitmap = await createImageBitmap(blob, {
            premultiplyAlpha: 'none',
            colorSpaceConversion: 'none'
          });
        } catch {
          bitmap = await createImageBitmap(blob);
        }

        this.put(url, bitmap);
        return bitmap;
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          // Transient network error
        }
        return null;
      } finally {
        this.inFlight.delete(url);
      }
    })();

    this.inFlight.set(url, promise);
    return promise;
  }

  /**
   * Preloads a list of single-image WebP URLs in the background.
   */
  public preloadMany(urls: string[]): void {
    for (const url of urls) {
      if (!url || this.cache.has(url) || this.inFlight.has(url) || this.failedUrls.has(url)) {
        continue;
      }
      void this.load(url);
    }
  }

  /**
   * Derives a model-level key from a single-image WebP URL so bounds.json is only fetched once per (product, model).
   */
  public getModelStemKey(webpUrl: string): string {
    const clean = webpUrl.split('?')[0];
    // Strip date, hour, and resolution suffix to group by product+model
    return clean
      .replace(/_\d{4}-\d{2}-\d{2}(?:_[A-Za-z0-9_]+)?_(?:1024|2048|4096)\.webp$/, '')
      .replace(/_(?:1024|2048|4096)\.webp$/, '');
  }

  public getCachedBounds(webpUrl: string): QuadCoordinates | null {
    const key = this.getModelStemKey(webpUrl);
    return this.boundsCache.get(key) || null;
  }

  /**
   * Optionally fetches the companion _bounds.json for a single-image WebP URL once per (product, model)
   * to guarantee exact geographic corner coordinates.
   */
  public async loadBoundsForWebpUrl(webpUrl: string): Promise<QuadCoordinates | null> {
    if (!webpUrl || !webpUrl.includes('.webp')) return null;
    const key = this.getModelStemKey(webpUrl);
    const existing = this.boundsCache.get(key);
    if (existing) return existing;

    const inflight = this.boundsInFlight.get(key);
    if (inflight) return inflight;

    const boundsUrl = webpUrl.replace(/_(?:1024|2048|4096)\.webp(\?.*)?$/, '_bounds.json$1');
    if (boundsUrl === webpUrl) return null;

    const p = (async (): Promise<QuadCoordinates | null> => {
      try {
        const res = await authenticatedFetch(boundsUrl);
        if (!res.ok) return null;
        const data = await res.json();
        if (Array.isArray(data?.coordinates) && data.coordinates.length === 4) {
          const coords = data.coordinates as QuadCoordinates;
          this.boundsCache.set(key, coords);
          return coords;
        }
        return null;
      } catch {
        return null;
      } finally {
        this.boundsInFlight.delete(key);
      }
    })();

    this.boundsInFlight.set(key, p);
    return p;
  }

  private put(url: string, bitmap: ImageBitmap): void {
    if (this.cache.has(url)) {
      const old = this.cache.get(url);
      if (old && old !== bitmap) {
        try {
          old.close();
        } catch {}
      }
      this.cache.delete(url);
    }

    this.cache.set(url, bitmap);

    while (this.cache.size > this.maxEntries) {
      const oldestKey = this.cache.keys().next().value;
      if (!oldestKey) break;
      const oldestBitmap = this.cache.get(oldestKey);
      this.cache.delete(oldestKey);
      if (oldestBitmap) {
        try {
          oldestBitmap.close();
        } catch {}
      }
    }
  }

  public clear(): void {
    for (const bmp of this.cache.values()) {
      try {
        bmp.close();
      } catch {}
    }
    this.cache.clear();
    this.inFlight.clear();
  }
}

export const imageBitmapCache = new ImageBitmapCacheManager();
export default imageBitmapCache;

